import QRCode from "qrcode";

/**
 * Generate QR code as data URL
 */
export async function generateQRCodeDataUrl(url: string): Promise<string> {
  return await QRCode.toDataURL(url, {
    width: 512,
    margin: 2,
    color: { dark: "#000000", light: "#ffffff" },
  });
}

/**
 * Pre-load a logo image for reuse across multiple labels
 */
export async function preloadLogoImage(logoUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const logoImg = new Image();
    logoImg.crossOrigin = "anonymous";
    
    const timeoutId = setTimeout(() => {
      console.log("[Label Preload] Logo load timeout");
      resolve(null);
    }, 5000);

    logoImg.onload = () => {
      clearTimeout(timeoutId);
      console.log(`[Label Preload] Logo loaded: ${logoImg.naturalWidth}x${logoImg.naturalHeight}`);
      resolve(logoImg);
    };
    logoImg.onerror = () => {
      clearTimeout(timeoutId);
      console.log("[Label Preload] Logo load failed");
      resolve(null);
    };
    logoImg.src = logoUrl;
  });
}

/**
 * Draw full printable label to canvas (4"x2" at 300 DPI = 1200x600px)
 * Avery 5163 landscape format - New 3-row grid design
 * @param logoImg - Pre-loaded Endless Storage logo image (use preloadLogoImage) or null
 */
export async function drawLabelToCanvas(
  boxNumber: string,
  qrDataUrl: string,
  logoImg?: HTMLImageElement | null
): Promise<string> {
  const drawStart = performance.now();
  console.log("[Label Draw] Starting label creation for:", boxNumber);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  // 4" x 2" at 300 DPI (landscape orientation for Avery 5163)
  const WIDTH = 1200;
  const HEIGHT = 600;
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  console.log("[Label Draw] Canvas dimensions:", WIDTH, "x", HEIGHT);

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Row heights
  const ROW1_HEIGHT = 120; // Instruction header row
  const ROW3_HEIGHT = 100; // Bottom branding row
  const ROW2_HEIGHT = HEIGHT - ROW1_HEIGHT - ROW3_HEIGHT; // Middle row (380px)

  // Column widths
  const COL1_WIDTH = 400; // Left column (instructions/QR/logo)
  const COL2_WIDTH = 400; // Middle column (for top row only)
  const COL3_WIDTH = 400; // Right column (for top row only)

  // Y positions
  const ROW1_Y = 0;
  const ROW2_Y = ROW1_HEIGHT;
  const ROW3_Y = ROW1_HEIGHT + ROW2_HEIGHT;

  // Border settings
  const BORDER_WIDTH = 2;
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = BORDER_WIDTH;

  // === DRAW GRID LINES ===
  
  // Outer border
  ctx.strokeRect(BORDER_WIDTH / 2, BORDER_WIDTH / 2, WIDTH - BORDER_WIDTH, HEIGHT - BORDER_WIDTH);

  // Horizontal dividers
  ctx.beginPath();
  ctx.moveTo(0, ROW2_Y);
  ctx.lineTo(WIDTH, ROW2_Y);
  ctx.moveTo(0, ROW3_Y);
  ctx.lineTo(WIDTH, ROW3_Y);
  ctx.stroke();

  // Vertical dividers in top row (3 columns)
  ctx.beginPath();
  ctx.moveTo(COL1_WIDTH, ROW1_Y);
  ctx.lineTo(COL1_WIDTH, ROW2_Y);
  ctx.moveTo(COL1_WIDTH + COL2_WIDTH, ROW1_Y);
  ctx.lineTo(COL1_WIDTH + COL2_WIDTH, ROW2_Y);
  ctx.stroke();

  // Vertical divider in middle row (QR | Room/Notes)
  ctx.beginPath();
  ctx.moveTo(COL1_WIDTH, ROW2_Y);
  ctx.lineTo(COL1_WIDTH, ROW3_Y);
  ctx.stroke();

  // Vertical divider in bottom row (Logo | Box Number)
  ctx.beginPath();
  ctx.moveTo(COL1_WIDTH, ROW3_Y);
  ctx.lineTo(COL1_WIDTH, HEIGHT);
  ctx.stroke();

  // === ROW 1: INSTRUCTION HEADER ===
  const instructionCenterY = ROW1_Y + ROW1_HEIGHT / 2;
  
  // Column 1: "1. Scan"
  ctx.font = "bold 36px Inter, sans-serif";
  ctx.fillStyle = "#000000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("1. Scan", COL1_WIDTH / 2, instructionCenterY - 20);
  ctx.font = "400 20px Inter, sans-serif";
  ctx.fillStyle = "#6B7280";
  ctx.fillText("QR code to catalog box", COL1_WIDTH / 2, instructionCenterY + 20);

  // Column 2: "2. Snap"
  ctx.font = "bold 36px Inter, sans-serif";
  ctx.fillStyle = "#000000";
  ctx.fillText("2. Snap", COL1_WIDTH + COL2_WIDTH / 2, instructionCenterY - 20);
  ctx.font = "400 20px Inter, sans-serif";
  ctx.fillStyle = "#6B7280";
  ctx.fillText("Photo contents before sealing", COL1_WIDTH + COL2_WIDTH / 2, instructionCenterY + 20);

  // Column 3: "Seal"
  ctx.font = "bold 36px Inter, sans-serif";
  ctx.fillStyle = "#000000";
  ctx.fillText("Seal", COL1_WIDTH + COL2_WIDTH + COL3_WIDTH / 2, instructionCenterY - 20);
  ctx.font = "400 20px Inter, sans-serif";
  ctx.fillStyle = "#6B7280";
  ctx.fillText("Securely pack and seal", COL1_WIDTH + COL2_WIDTH + COL3_WIDTH / 2, instructionCenterY + 12);
  ctx.fillText("your box", COL1_WIDTH + COL2_WIDTH + COL3_WIDTH / 2, instructionCenterY + 36);

  // === ROW 2: QR CODE + ROOM/NOTES ===
  
  // Load and draw QR code
  console.log("[Label Draw] Loading QR image...");
  const qrLoadStart = performance.now();
  const qrImg = new Image();
  await new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("QR image load timeout"));
    }, 5000);

    qrImg.onload = () => {
      clearTimeout(timeoutId);
      console.log(
        `[Label Draw] QR image loaded in ${(performance.now() - qrLoadStart).toFixed(2)}ms`
      );
      resolve();
    };
    qrImg.onerror = (err) => {
      clearTimeout(timeoutId);
      console.error("[Label Draw] QR image failed to load:", err);
      reject(err);
    };
    qrImg.src = qrDataUrl;
  });

  // Draw QR code centered in left column of middle row
  const qrSize = 300;
  const qrX = (COL1_WIDTH - qrSize) / 2;
  const qrY = ROW2_Y + (ROW2_HEIGHT - qrSize) / 2;
  ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
  console.log("[Label Draw] QR drawn at position:", qrX, qrY, "size:", qrSize);

  // Room/Notes label
  ctx.font = "bold 32px Inter, sans-serif";
  ctx.fillStyle = "#000000";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("Room/Notes:", COL1_WIDTH + 30, ROW2_Y + 30);

  // Draw a subtle dotted line area for writing
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = "#D1D5DB";
  ctx.lineWidth = 1;
  const notesAreaTop = ROW2_Y + 80;
  const notesAreaHeight = ROW2_HEIGHT - 100;
  const lineSpacing = 50;
  for (let y = notesAreaTop + lineSpacing; y < notesAreaTop + notesAreaHeight; y += lineSpacing) {
    ctx.beginPath();
    ctx.moveTo(COL1_WIDTH + 30, y);
    ctx.lineTo(WIDTH - 30, y);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  // === ROW 3: LOGO + BOX NUMBER ===
  
  // Draw Endless Storage logo if provided
  console.log("[Label Draw] Logo image:", logoImg ? "present" : "none");
  if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
    try {
      const maxLogoHeight = 60;
      const maxLogoWidth = 350;
      let logoHeight = logoImg.naturalHeight;
      let logoWidth = logoImg.naturalWidth;

      if (logoHeight > maxLogoHeight) {
        logoWidth = (logoWidth * maxLogoHeight) / logoHeight;
        logoHeight = maxLogoHeight;
      }
      if (logoWidth > maxLogoWidth) {
        logoHeight = (logoHeight * maxLogoWidth) / logoWidth;
        logoWidth = maxLogoWidth;
      }

      // Center logo in bottom-left cell
      const logoX = (COL1_WIDTH - logoWidth) / 2;
      const logoY = ROW3_Y + (ROW3_HEIGHT - logoHeight) / 2;
      ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
      console.log("[Label Draw] Logo drawn at:", logoX, logoY, "scaled to:", logoWidth, "x", logoHeight);
    } catch (err) {
      console.error("[Label Draw] Logo error:", err);
    }
  }

  // Box number in bottom-right area
  ctx.font = 'bold 48px "Roboto Mono", monospace';
  ctx.fillStyle = "#000000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const boxNumberX = COL1_WIDTH + (WIDTH - COL1_WIDTH) / 2;
  const boxNumberY = ROW3_Y + ROW3_HEIGHT / 2;
  ctx.fillText(boxNumber, boxNumberX, boxNumberY);
  console.log("[Label Draw] Box number drawn:", boxNumber);

  console.log("[Label Draw] Converting canvas to data URL...");
  const dataUrl = canvas.toDataURL("image/png");
  console.log(
    `[Label Draw] Label complete in ${(performance.now() - drawStart).toFixed(2)}ms, data URL length: ${dataUrl.length}`
  );

  return dataUrl;
}

/**
 * Generate labels and package into ZIP or PDF
 */
export async function generateLabelsPackage(
  labels: Array<{ code: string; labelDataUrl: string }>,
  exportFormat: "zip" | "pdf",
  filenamePrefix: string
): Promise<Blob> {
  if (exportFormat === "pdf") {
    // Dynamic import to avoid loading jspdf when not needed
    const { jsPDF } = await import("jspdf");

    // Avery 5163 layout (2 columns x 5 rows = 10 labels per sheet)
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "in",
      format: "letter",
    });

    const COLS = 2;
    const ROWS = 5;
    const LABELS_PER_PAGE = COLS * ROWS;
    const LABEL_WIDTH = 4;
    const LABEL_HEIGHT = 2;
    const MARGIN_LEFT = 0.15625;
    const MARGIN_TOP = 0.5;
    const GAP_X = 0.1875;

    for (let i = 0; i < labels.length; i++) {
      const positionOnPage = i % LABELS_PER_PAGE;
      const col = positionOnPage % COLS;
      const row = Math.floor(positionOnPage / COLS);

      if (i > 0 && positionOnPage === 0) {
        pdf.addPage();
      }

      const x = MARGIN_LEFT + col * (LABEL_WIDTH + GAP_X);
      const y = MARGIN_TOP + row * LABEL_HEIGHT;

      pdf.addImage(labels[i].labelDataUrl, "PNG", x, y, LABEL_WIDTH, LABEL_HEIGHT);
    }

    return pdf.output("blob");
  } else {
    // ZIP format
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();

    for (const { code, labelDataUrl } of labels) {
      const base64 = labelDataUrl.split(",")[1];
      zip.file(`${code}.png`, base64, { base64: true });
    }

    return await zip.generateAsync({ type: "blob" });
  }
}
